import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    CircularProgress,
    Alert,
    FormControl,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    Stack,
    Modal,
} from "@mui/material";
import SideBar from "../Dashboard/StudentDashboard/Sidebar/SideBar";
import {
    useGetQuizQuestionByQuizIdListMutation,
    useGetQuizQuestionListMutation,
    useGetQuizResponseMutation,
} from "../../apiSlice";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "../StudentBreadcrumb/Breadcrumb";
import JSConfetti from "js-confetti";

const QuizContent = () => {
    const navigate = useNavigate();

    // const location = useLocation();
    // const {quizId} = location.state || {};

    // console.log(quizId);

    const [questions, setQuestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [isLastQuestion, setIsLastQuestion] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [loading, setLoading] = useState({
        questions: true,
        submission: false,
    });
    const [error, setError] = useState(null);
    const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
    const [studentQuizQuestionsByIdAPI] =
        useGetQuizQuestionByQuizIdListMutation();
    const [submitQuiz] = useGetQuizResponseMutation();
    const [jsConfetti, setJsConfetti] = useState(null);

    useEffect(() => {
        setJsConfetti(new JSConfetti());
    }, []);

    // Fetch quiz questions
    useEffect(() => {
        /**
         * Taking "quizId" from local storage
         */
        const quizId = Number(localStorage.getItem("quizId"));

        const loadQuiz = async () => {
            try {
                const response = await studentQuizQuestionsByIdAPI(quizId);
                setQuestions(response.data || []);
            } catch (err) {
                setError(
                    "Failed to load quiz questions. Please try again later."
                );
                console.error("Fetch error:", err);
            } finally {
                setLoading((prev) => ({ ...prev, questions: false }));
            }
        };
        loadQuiz();
    }, []);

    // Timer logic
    useEffect(() => {
        if (timeLeft <= 0) {
            handleSubmit();
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    const handleAnswerSelect = (questionId, answer) => {
        setSelectedAnswers((prev) => ({
            ...prev,
            [questionId]: answer,
        }));
    };

    const handleQuestionNavigation = (index) => {
        setCurrentIndex(index);
    };

    const handleSubmit = async () => {
        try {
            setLoading((prev) => ({ ...prev, submission: true }));

            // Create the properly formatted request payload
            const quizResponseList = questions.map((question) => ({
                quizResponseId: 0,
                quizId: question.quizId || 0,
                quizQuestionId: question.quizQuestionId,
                quizAnswerId: 0,
                quizUserResponse:
                    selectedAnswers[question.quizQuestionId] || "",
                quizAnswerPoint: 20,
                isAttempted: true,
            }));

            console.log("Submitting quiz with payload:", quizResponseList);

            const response = await submitQuiz(quizResponseList);
            console.log("i am response", response);

            // Trigger confetti
            if (jsConfetti) {
                jsConfetti.addConfetti({
                    confettiColors: [
                        "#FF5E78",
                        "#5AC8FA",
                        "#85E065",
                        "#FF9500",
                        "#8E44AD",
                        "#FF073A",
                    ],
                });
            }

            // Small delay to show confetti before navigation
            setTimeout(() => {
                navigate(
                    "/StudentDashboard/Quiz/QuizContent/QuizCompletionPage",
                    {
                        state: {
                            questions,
                            userAnswers: selectedAnswers,
                            apiResponse: response.data,
                        },
                    }
                );
            }, 1000);
        } catch (err) {
            console.error("Submission error:", err);
            setError(
                err.data?.message ||
                    "Failed to submit quiz. Please check your answers and try again."
            );
        } finally {
            setLoading((prev) => ({ ...prev, submission: false }));
        }
    };

    if (loading.questions && !questions.length) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                    {error.includes("validation") && (
                        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                            {Object.entries(error.errors || {}).map(
                                ([field, messages]) =>
                                    messages.map((message, i) => (
                                        <li key={`${field}-${i}`}>{message}</li>
                                    ))
                            )}
                        </Box>
                    )}
                </Alert>
                <Button
                    variant="contained"
                    onClick={() => window.location.reload()}
                    sx={{ mt: 2 }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    if (!questions.length) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h6">
                    No questions available for this quiz
                </Typography>
            </Box>
        );
    }

    const currentQuestion = questions[currentIndex];
    const options = currentQuestion?.quizOptions?.[0] || {};

    return (
        <>
            <SideBar />
            <Box
                sx={{
                    flexGrow: 1,
                    position: "relative",
                    width: "calc(100vw - 60px)",
                    padding: "20px",
                    height: "100vh",
                    left: "60px",
                    backgroundColor: "#F8F9FD",
                }}
            >
                <Breadcrumb />
                <Box sx={{ display: "flex" }}>
                    {/* Questions Index Sidebar */}
                    <Paper
                        sx={{
                            width: 250,
                            p: 2,
                            overflow: "auto",
                            borderRadius: 0,
                            borderRight: "1px solid #e0e0e0",
                            height: "calc(100vh - 84px)",
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ mb: 2, fontWeight: "bold" }}
                        >
                            Questions
                        </Typography>
                        <Divider />
                        <List>
                            {questions.map((question, index) => (
                                <ListItem
                                    key={question.quizQuestionId}
                                    disablePadding
                                    sx={{ mb: 1 }}
                                >
                                    <ListItemButton
                                        selected={currentIndex === index}
                                        onClick={() =>
                                            handleQuestionNavigation(index)
                                        }
                                        sx={{
                                            borderRadius: 1,
                                            bgcolor:
                                                currentIndex === index
                                                    ? "#e3f2fd"
                                                    : "transparent",
                                            "&:hover": { bgcolor: "#f1f1f1" },
                                        }}
                                    >
                                        <ListItemText
                                            primary={`Question ${index + 1}`}
                                            secondary={
                                                selectedAnswers[
                                                    question.quizQuestionId
                                                ]
                                                    ? "Answered"
                                                    : "Unanswered"
                                            }
                                            secondaryTypographyProps={{
                                                color: selectedAnswers[
                                                    question.quizQuestionId
                                                ]
                                                    ? "success.main"
                                                    : "text.secondary",
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>

                    {/* Main Quiz Content */}
                    <Box
                        sx={{
                            flex: 1,
                            p: 3,
                            overflow: "auto",
                        }}
                    >
                        <Box
                            sx={{
                                bgcolor: "background.paper",
                                p: 3,
                                borderRadius: 2,
                                boxShadow: 1,
                                maxWidth: 800,
                                mx: "auto",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 3,
                                }}
                            >
                                <Typography variant="h6">
                                    Question {currentIndex + 1} of{" "}
                                    {questions.length}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color={
                                        timeLeft < 60
                                            ? "error.main"
                                            : "text.primary"
                                    }
                                >
                                    Time Left: {formatTime(timeLeft)}
                                </Typography>
                            </Box>

                            <Typography variant="h5" gutterBottom>
                                {currentQuestion.quizQuestion1}
                            </Typography>

                            <FormControl
                                component="fieldset"
                                sx={{ mt: 2, width: "100%" }}
                            >
                                <RadioGroup
                                    value={
                                        selectedAnswers[
                                            currentQuestion.quizQuestionId
                                        ] || ""
                                    }
                                    onChange={(e) =>
                                        handleAnswerSelect(
                                            currentQuestion.quizQuestionId,
                                            e.target.value
                                        )
                                    }
                                >
                                    {[1, 2, 3, 4].map((optNum) => (
                                        <FormControlLabel
                                            key={optNum}
                                            value={
                                                options[`quizOption${optNum}`]
                                            }
                                            control={<Radio />}
                                            onClick={() => {
                                                currentIndex >=
                                                    questions.length - 1 &&
                                                    setIsLastQuestion(false);
                                            }}
                                            label={
                                                options[`quizOption${optNum}`]
                                            }
                                            sx={{
                                                mb: 1,
                                                p: 1,
                                                borderRadius: 1,
                                                "&:hover": {
                                                    bgcolor: "#f5f5f5",
                                                },
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mt: 4,
                                    pt: 2,
                                    borderTop: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() =>
                                        handleQuestionNavigation(
                                            currentIndex - 1
                                        )
                                    }
                                    disabled={currentIndex === 0}
                                >
                                    Previous
                                </Button>

                                {currentIndex < questions.length - 1 ? (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() =>
                                            handleQuestionNavigation(
                                                currentIndex + 1
                                            )
                                        }
                                        disabled={
                                            !selectedAnswers[
                                                currentQuestion.quizQuestionId
                                            ]
                                        }
                                    >
                                        Next
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={handleSubmit}
                                        // disabled={loading.submission}
                                        disabled={isLastQuestion}
                                    >
                                        {loading.submission ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            "Submit Quiz"
                                        )}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default QuizContent;
